// src/components/MedicalWorkspace.js
import React from 'react';
import { useParams } from 'react-router-dom';
import FormularioClinico from './clinica/FormularioClinico';
import ResumenFicha from './clinica/ResumenFicha';
import { useClinica } from '../context/ClinicaContext';
import { format } from 'date-fns';
import { useMedicalWorkspace } from '../hooks/useMedicalWorkspace';

import {
    Dashboard as DashboardIcon,
    CalendarMonth as CalendarIcon,
    Person as PersonIcon,
    Pause as PauseIcon,
    NotInterested as AbsentIcon,
    ArrowBack as ReturnIcon,
    CheckCircle as SuccessIcon,
    Lock as LockIcon
} from '@mui/icons-material';

export default function MedicalWorkspace() {
    const { consultaId } = useParams();
    const { definicion, termino } = useClinica();

    // 1. INYECTAMOS EL CEREBRO DE LA ESTACIÓN CLÍNICA
    const {
        consulta, historialPaciente, loading,
        visitaHistoricaSeleccionada, setVisitaHistoricaSeleccionada,
        showConfirmFinish, setShowConfirmFinish,
        showPostSaveOptions,
        showExitOptions, setShowExitOptions,
        procesandoEstado, handleMutarEstadoConsulta, handleFinalizarYSellar,
        navigate, showToast
    } = useMedicalWorkspace(consultaId);

    if (loading) {
        return (
            <div className="h-screen bg-slate-900 flex items-center justify-center text-white flex-col gap-4">
                <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xs font-bold tracking-widest text-slate-400 uppercase">Aislando Entorno Clínico...</span>
            </div>
        );
    }

    const paciente = consulta?.paciente || {};
    const pacienteId = paciente.id || paciente._id;
    const esConsultaCompletada = consulta?.estado === 'completada';
    const plantilla = definicion(consulta?.especialidad);

    return (
        <div className="h-screen flex flex-col bg-slate-50 overflow-hidden relative">

            <header className="h-16 bg-slate-900 text-white flex items-center justify-between px-4 md:px-6 gap-3 shrink-0 shadow-md z-20">
                <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center font-black text-emerald-400">
                        {paciente.nombre?.[0] || '?'}{paciente.apellidos?.[0] || '?'}
                    </div>
                    <div>
                        <h1 className="text-lg font-black tracking-tight leading-none">
                            {paciente.nombre !== 'Desconocido' ? `${paciente.nombre} ${paciente.apellidos}` : 'Paciente sin identificar'}
                        </h1>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            DNI: {paciente.dni || '---'} • {paciente.seguroMedico?.toUpperCase() || 'PRIVADO'}
                            {plantilla && <> • <span style={{ color: plantilla.color }}>{plantilla.nombre}</span></>}
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    {esConsultaCompletada ? (
                        <span className="flex items-center gap-2 text-xs font-bold text-slate-400 bg-slate-800 px-3 py-1.5 rounded-full border border-slate-700 shadow-inner">
                            <LockIcon fontSize="small" /> Sellado (Solo Lectura)
                        </span>
                    ) : (
                        <>
                            <span className="animate-pulse hidden sm:flex items-center gap-2 text-xs font-bold text-emerald-400 bg-emerald-400/10 px-3 py-1.5 rounded-full border border-emerald-400/20">
                                <span className="w-2 h-2 rounded-full bg-emerald-400"></span> {termino('consulta')} en curso
                            </span>
                            <button
                                onClick={() => setShowExitOptions(true)}
                                className="px-4 py-2 bg-slate-800 hover:bg-red-500/20 hover:text-red-400 text-slate-300 text-sm font-bold rounded-xl transition-colors"
                            >
                                Salir / Pausar
                            </button>
                        </>
                    )}
                </div>
            </header>

            <div className="flex flex-1 overflow-hidden">
                {/* Historial lateral: oculto en móvil (el médico se centra en el
                    formulario); visible a partir de lg. */}
                <aside className="hidden lg:flex w-[320px] bg-white border-r border-slate-200 flex-col overflow-hidden shrink-0 z-10 shadow-[4px_0_24px_rgba(0,0,0,0.02)]">
                    <div className="p-5 border-b border-slate-100 bg-slate-50/50">
                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Motivo del Acto Actual</h3>
                        <p className="text-sm font-bold text-slate-800 leading-relaxed bg-white p-3 rounded-xl border border-slate-200">
                            {consulta?.motivo || 'No especificado en el triaje.'}
                        </p>
                    </div>

                    <div className="flex-1 overflow-y-auto p-5">
                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Historial Clínico</h3>
                        <div className="space-y-3">
                            {historialPaciente.filter(c => c.id !== consultaId && c._id !== consultaId).map((visita, idx) => (
                                <button
                                    key={visita.id || idx}
                                    onClick={() => setVisitaHistoricaSeleccionada(visita)}
                                    className="w-full text-left p-4 border border-slate-100 rounded-2xl hover:bg-slate-50 hover:border-slate-300 transition-all group focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                                >
                                    <div className="flex items-center justify-between mb-1.5">
                                        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">
                                            {format(new Date(visita.fecha), 'dd MMM, yyyy')}
                                        </p>
                                        <svg className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                                    </div>
                                    <p className="text-xs font-bold text-slate-800 line-clamp-2">
                                        {visita.diagnostico || visita.motivo}
                                    </p>
                                </button>
                            ))}
                            {historialPaciente.length <= 1 && (
                                <p className="text-xs text-slate-400 text-center py-4 font-semibold">Paciente de primera visita.</p>
                            )}
                        </div>
                    </div>
                </aside>

                <main className="flex-1 overflow-y-auto bg-slate-50 p-6 md:p-8 relative">
                    <div className="max-w-5xl mx-auto relative">
                        {esConsultaCompletada && (
                            <div className="mb-6 p-5 bg-amber-50/80 border border-amber-200 rounded-2xl flex items-start gap-4 shadow-sm">
                                <LockIcon className="text-amber-500 shrink-0 mt-0.5" />
                                <div>
                                    <h3 className="text-sm font-black text-amber-900 uppercase tracking-widest">Acto Médico Sellado</h3>
                                    <p className="text-sm font-semibold text-amber-800 mt-1 leading-relaxed">
                                        Este triaje ha sido firmado y registrado. Para garantizar la trazabilidad legal, el expediente es ahora <strong>inmutable</strong>.
                                    </p>
                                    <button onClick={() => navigate(`/pacientes/${pacienteId}`)} className="mt-3 text-xs font-black text-amber-700 hover:text-amber-900 uppercase tracking-wider underline underline-offset-4">
                                        Volver al Directorio del Paciente
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* La ficha ya desactiva sus campos si está sellada; las pestañas siguen navegables para consultarla */}
                        <div className={esConsultaCompletada ? "grayscale-[20%]" : ""}>
                            <FormularioClinico
                                consultaOriginal={consulta}
                                alGuardar={() => setShowConfirmFinish(true)}
                                mostrarToast={showToast}
                            />
                        </div>
                    </div>
                </main>
            </div>

            {/* MODAL 1: CONFIRMAR FINALIZACIÓN */}
            {showConfirmFinish && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fade-in" style={{ margin: 0 }}>
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 border border-slate-200 text-center">
                        <div className="w-16 h-16 bg-amber-100 text-amber-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner">
                            <LockIcon fontSize="large" />
                        </div>
                        <h3 className="text-xl font-black text-slate-900 mb-2">¿Finalizar Consulta?</h3>
                        <p className="text-sm font-medium text-slate-500 mb-6">
                            Los datos clínicos se guardarán. Si decides finalizar, el historial se sellará legalmente y no podrá ser modificado.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setShowConfirmFinish(false)}
                                className="px-5 py-2.5 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                                disabled={procesandoEstado}
                            >
                                Volver a editar
                            </button>
                            <button
                                onClick={handleFinalizarYSellar}
                                className="px-5 py-2.5 text-sm font-bold text-white bg-amber-500 hover:bg-amber-600 rounded-xl transition-colors shadow-md flex items-center justify-center min-w-[140px]"
                                disabled={procesandoEstado}
                            >
                                {procesandoEstado ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : 'Sí, Sellar Triaje'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL 2: A DÓNDE IR TRAS EL SELLADO */}
            {showPostSaveOptions && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-emerald-900/80 backdrop-blur-md p-4 animate-fade-in" style={{ margin: 0 }}>
                    <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 border border-emerald-100 text-center">
                        <div className="w-20 h-20 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner"><SuccessIcon style={{ fontSize: 40 }} /></div>
                        <h3 className="text-2xl font-black text-slate-900 mb-2">Acto Médico Sellado con Éxito</h3>
                        <p className="text-sm font-medium text-slate-500 mb-8 leading-relaxed">La firma digital e inmutabilidad han sido procesadas. Seleccione su próximo destino clínico:</p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <button onClick={() => navigate('/dashboard')} className="flex flex-col items-center gap-2 p-5 rounded-2xl border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 transition-all text-blue-700">
                                <DashboardIcon /><span className="font-bold text-sm">Dashboard</span>
                            </button>
                            <button onClick={() => navigate('/agenda')} className="flex flex-col items-center gap-2 p-5 rounded-2xl border-2 border-slate-100 hover:border-indigo-500 hover:bg-indigo-50 transition-all text-indigo-700">
                                <CalendarIcon /><span className="font-bold text-sm">Agenda</span>
                            </button>
                            <button onClick={() => navigate(`/pacientes/${pacienteId}`)} className="sm:col-span-2 flex flex-col items-center gap-2 p-5 rounded-2xl border-2 border-slate-100 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-emerald-700">
                                <PersonIcon /><span className="font-bold text-sm">Volver a la Ficha del Paciente</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL SALIR / PAUSAR */}
            {showExitOptions && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fade-in" style={{ margin: 0 }}>
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 border border-slate-200">
                        <h3 className="text-xl font-black text-slate-900 mb-1">Pausar o Abandonar</h3>
                        <p className="text-sm font-medium text-slate-500 mb-6">El paciente está actualmente asignado a su Box. ¿Qué desea hacer con el estado de esta cita?</p>

                        <div className="space-y-3">
                            <button onClick={() => handleMutarEstadoConsulta('en_curso')} disabled={procesandoEstado} className="w-full flex items-center gap-4 p-4 rounded-2xl border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-all text-left">
                                <PauseIcon className="text-blue-500" />
                                <div><p className="font-bold text-slate-800 text-sm">Pausar y Salir al Dashboard</p><p className="text-xs text-slate-500 mt-0.5">La cita seguirá &apos;En Curso&apos;.</p></div>
                            </button>
                            <button onClick={() => handleMutarEstadoConsulta('pendiente')} disabled={procesandoEstado} className="w-full flex items-center gap-4 p-4 rounded-2xl border border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-all text-left">
                                <ReturnIcon className="text-slate-500" />
                                <div><p className="font-bold text-slate-800 text-sm">Devolver a Sala de Espera</p><p className="text-xs text-slate-500 mt-0.5">El paciente vuelve a estar disponible.</p></div>
                            </button>
                            <button onClick={() => handleMutarEstadoConsulta('ausente')} disabled={procesandoEstado} className="w-full flex items-center gap-4 p-4 rounded-2xl border border-slate-200 hover:border-red-400 hover:bg-red-50 transition-all text-left">
                                <AbsentIcon className="text-red-500" />
                                <div><p className="font-bold text-slate-800 text-sm">Marcar como Ausente</p><p className="text-xs text-slate-500 mt-0.5">El paciente abandonó el recinto.</p></div>
                            </button>
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button onClick={() => setShowExitOptions(false)} className="px-5 py-2 font-bold text-sm text-slate-500 hover:bg-slate-100 rounded-xl transition-colors">Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* SLIDER DEL HISTÓRICO */}
            {visitaHistoricaSeleccionada && (
                <div
                    className="absolute inset-0 z-40 flex justify-end bg-slate-900/20 backdrop-blur-[2px] animate-fade-in"
                    onClick={() => setVisitaHistoricaSeleccionada(null)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape' || e.key === 'Enter') setVisitaHistoricaSeleccionada(null);
                    }}
                >
                    <div
                        className="w-full max-w-[500px] h-full bg-white shadow-2xl border-l border-slate-200 flex flex-col transform transition-transform animate-slide-in-right"
                        onClick={(e) => e.stopPropagation()}
                        role="presentation"
                        onKeyDown={(e) => e.stopPropagation()}
                    >
                        <div className="px-6 py-5 bg-slate-900 text-white flex items-center justify-between shrink-0">
                            <div>
                                <h3 className="text-lg font-black tracking-tight">Acto Clínico Previo</h3>
                                <p className="text-xs font-semibold text-slate-400 mt-0.5">{format(new Date(visitaHistoricaSeleccionada.fecha), "dd 'de' MMMM, yyyy - HH:mm")} hrs</p>
                            </div>
                            <button onClick={() => setVisitaHistoricaSeleccionada(null)} className="p-2 text-slate-400 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-slate-50/50">
                            <ResumenFicha consulta={visitaHistoricaSeleccionada} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}